import { supabase } from '../../supabase';
import { getAccessToken } from './getAccessToken';
import { RUNTIME_PROFILES, type PersonalityType } from '../../personalities/runtimeProfiles';

// Track which provider was used (for debugging and cost tracking)
type AIProvider = 'gemini' | 'openai';
let lastProviderUsed: AIProvider | null = null;

export function getLastProviderUsed(): AIProvider | null {
  return lastProviderUsed;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  created_at?: string;
}

export interface ChatSession {
  id: string;
  user_id: string;
  session_start: string;
  session_end?: string;
  message_count: number;
  user_message_count: number;
  ai_message_count: number;
  session_duration_seconds?: number;
}

/**
 * Get chat history for a user with optional context filter
 * @param userId - User ID
 * @param limitOrContext - Either a number (limit) or string (context), defaults to 20 if number
 * @param actualLimit - Used when limitOrContext is a context string
 */
export async function getChatHistory(
  userId: string,
  limitOrContext: number | string = 20,
  actualLimit?: number
): Promise<ChatMessage[]> {
  // Parse parameters for backwards compatibility
  const isContextProvided = typeof limitOrContext === 'string';
  const context = isContextProvided ? limitOrContext : undefined;
  const limit = isContextProvided ? (actualLimit ?? 20) : limitOrContext;

  let query = supabase
    .from('journal_chat_messages')
    .select('role, content, created_at')
    .eq('user_id', userId);

  // Filter by context if provided (assumes 'context' column exists)
  // For backwards compatibility, if context column doesn't exist yet, query will still work
  if (context) {
    query = query.eq('context', context);
  }

  const { data, error } = await query
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return (data?.reverse() || []) as ChatMessage[];
}

/**
 * Save a chat message to the database with optional context
 */
export async function saveChatMessage(
  userId: string,
  role: 'user' | 'assistant',
  content: string,
  context: string = 'journal'
): Promise<void> {
  const { error} = await supabase
    .from('journal_chat_messages')
    .insert({ user_id: userId, role, content, context });

  if (error) throw error;
}

/**
 * Clear chat history for a user, optionally filtered by context
 */
export async function clearChatHistory(userId: string, context?: string): Promise<void> {
  let query = supabase
    .from('journal_chat_messages')
    .delete()
    .eq('user_id', userId);

  // Filter by context if provided
  if (context) {
    query = query.eq('context', context);
  }

  const { error } = await query;

  if (error) throw error;
}

/**
 * Build the master system prompt with personality injection
 */
function buildSystemPrompt(personalityProfile: string): string {
  return `
LIFE RE:SCRIPTED - Journaling assistant

Core responsibilities:
- Listen and reflect; keep responses concise (100-150 tokens).
- Align insight with the active personality and ask 1-3 follow-up questions.
- Maintain emotional safety.

Response structure:
1) Reflection: brief paraphrase + acknowledge feelings/intentions.
2) Insight: personality-aligned perspective in 2-3 sentences.
3) Prompts: 1-3 focused follow-up questions that match the personality.

Active personality:
${personalityProfile}

Personality switching:
- Honor the app-provided active personality; acknowledge when it changes.
- If the user requests a switch, confirm whether it is for the session or one reply, then follow that scope.

Safety:
- Do not diagnose or give medical/legal/therapeutic instructions.
- If crisis language appears, respond with empathy and direct to hotlines/professionals/friends; keep language calm.

Cost control:
- Keep responses short; avoid quoting large blocks; keep questions concise.

Tool integration:
- Respect the provided context (journal, mindforge, habitforge, coreforge, wayfinder). If already inside a tool, act within it instead of suggesting they try it.
`.trim();
}

/**
 * Build journal context section for the prompt
 * Detects whether context is from Journal, MindForge, HabitForge, or CoreForge
 */
function buildJournalContext(recentJournals?: Record<string, unknown>[]): string {
  if (!recentJournals || recentJournals.length === 0) {
    return '';
  }

  const isMindForgeContext = recentJournals.some(j =>
    Boolean((j.entry_text as string | undefined)?.includes('Thought Pattern:')) || Boolean(j.thought_label)
  );

  const isHabitForgeContext = recentJournals.some(j =>
    (j.entry_text as string | undefined)?.includes('Habit:') && (j.entry_text as string | undefined)?.includes('Intention:')
  );

  const isCoreForgeContext = recentJournals.some(j =>
    (j.entry_text as string | undefined)?.includes('Core Belief:') && (j.entry_text as string | undefined)?.includes('Balanced Version:')
  );

  const isWayfinderContext = recentJournals.some(j =>
    Boolean((j.entry_text as string | undefined)?.includes('Life Map:')) || Boolean((j.entry_text as string | undefined)?.includes('Current Focus Card'))
  );

  const journalSummary = recentJournals
    .slice(0, 3)
    .map(j => {
      const date = (j.created_at as string | undefined)?.split('T')[0];
      const text = (j.entry_text as string | undefined) ?? (j.content as string | undefined) ?? '';
      const preview = text.substring(0, 150);
      const mood = j.mood ? `Mood: ${j.mood}/5` : '';
      const energy = j.energy ? `Energy: ${j.energy}/5` : '';
      return `${date}: ${preview}... ${mood} ${energy}`.trim();
    })
    .join('\n');

  if (isMindForgeContext) {
    return `
Context: mindforge (you are integrated into MindForge)
Recent thought reframes:
${journalSummary}

Do:
- Spot cognitive distortions and guide balanced reframes with CBT steps.
- Reference the recent reframes shown above.
- Ask questions that deepen awareness of patterns.

Avoid:
- Saying you are not part of MindForge.
- Inventing history beyond what is shown.
- Referencing entries older than this summary.`;
  }

  if (isHabitForgeContext) {
    return `
Context: habitforge (you are integrated into HabitForge)
Current habits:
${journalSummary}

Do:
- Use habit psychology (cue-routine-reward) and reference the habits above.
- Ask about obstacles and suggest small, achievable steps.
- Use pros/cons to strengthen commitment; suggest MindForge only for cognitive blockers.

Avoid:
- Saying you are not part of HabitForge.
- Guilt-driven advice or rigid schedules.
- Inventing history beyond what is shown.`;
  }

  if (isCoreForgeContext) {
    return `
Context: coreforge (you are integrated into CoreForge)
Active core beliefs:
${journalSummary}

Do:
- Explore and transform core beliefs gently; reference balanced versions shown above.
- Ask about origins and evidence for/against the beliefs with compassion.
- Suggest refining balanced beliefs when ready; note when MindForge could help with distortions.

Avoid:
- Saying you are not part of CoreForge.
- Rushing deep belief work or forcing positivity.
- Inventing history beyond what is shown.`;
  }

  if (isWayfinderContext) {
    return `
Context: wayfinder (you are integrated into Wayfinder)
Life Map and current Focus Card:
${journalSummary}

Do:
- Clarify purpose/values and connect them to 90-day focus planning.
- Break visions into actionable steps; celebrate progress.
- Ask questions that reconnect them to their "why."

Avoid:
- Saying you are not part of Wayfinder.
- Generic productivity tips that ignore purpose.
- Inventing history beyond what is shown.`;
  }

  return `
Context: journal
Recent entries:
${journalSummary}

Do:
- Reference patterns, mood/energy trends, and recurring themes.
- Celebrate progress or positive shifts.

Avoid:
- Inventing history beyond what is shown.
- Claiming long-term memory beyond this context.`;
}

async function callChatProvider(
  provider: AIProvider,
  userMessage: string,
  systemPrompt: string,
  history: ChatMessage[],
  _userId?: string
): Promise<string> {
  const accessToken = await getAccessToken();

  const messages = [
    { role: 'system' as const, content: systemPrompt },
    ...history.map(msg => ({
      role: msg.role as 'user' | 'assistant',
      content: msg.content
    })),
    { role: 'user' as const, content: userMessage }
  ];

  const response = await fetch('/api/chat', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify({
      provider,
      model: provider === 'openai' ? 'gpt-5-mini' : 'gemini-2.5-flash',
      messages,
      temperature: 0.9,
      max_tokens: 4096 // Increased to prevent cutoffs, allows longer responses
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(`API error (${provider}): ${errorData.error ?? 'Unknown error'}`);
  }

  const data = await response.json();
  const responseText = data.choices?.[0]?.message?.content;

  if (!responseText || responseText.trim().length === 0) {
    throw new Error(`${provider} returned empty response`);
  }

  return responseText;
}

/**
 * Get AI chat response with automatic fallback (OpenAI -> Gemini)
 */
export async function getAIChatResponse(
  userMessage: string,
  userId: string,
  personality: PersonalityType,
  recentJournals?: Record<string, unknown>[]
): Promise<string> {
  const personalityProfile = RUNTIME_PROFILES[personality];
  const systemPrompt = buildSystemPrompt(personalityProfile);
  const journalContext = buildJournalContext(recentJournals);
  const fullSystemPrompt = [systemPrompt, journalContext].filter(Boolean).join('\n\n');

  const history = await getChatHistory(userId, 5);

  try {
    const response = await callChatProvider('openai', userMessage, fullSystemPrompt, history, userId);
    lastProviderUsed = 'openai';
    return response;
  } catch (openaiError) {
    console.warn('OpenAI failed, falling back to Gemini', openaiError);
  }

  try {
    const response = await callChatProvider('gemini', userMessage, fullSystemPrompt, history, userId);
    lastProviderUsed = 'gemini';
    return response;
  } catch (geminiError) {
    console.error('Gemini failed', geminiError);
    throw new Error('All AI providers failed. Please try again later.');
  }
}

// ========== ACTIVITY TRACKING (No conversation content stored) ==========

/**
 * Start a new chat session for analytics tracking
 * Returns session ID to track throughout the conversation
 */
export async function startChatSession(userId: string): Promise<string> {
  const { data, error } = await supabase
    .from('chat_activity')
    .insert({
      user_id: userId,
      session_start: new Date().toISOString(),
      message_count: 0,
      user_message_count: 0,
      ai_message_count: 0
    })
    .select('id')
    .single();

  if (error) throw error;
  return data.id;
}

/**
 * Update chat session with message count (called after each message exchange)
 */
export async function updateChatActivity(
  sessionId: string,
  userMessageCount: number,
  aiMessageCount: number
): Promise<void> {
  const { error } = await supabase
    .from('chat_activity')
    .update({
      message_count: userMessageCount + aiMessageCount,
      user_message_count: userMessageCount,
      ai_message_count: aiMessageCount
    })
    .eq('id', sessionId);

  if (error) throw error;
}

/**
 * End a chat session and calculate duration
 */
export async function endChatSession(sessionId: string, sessionStartTime: Date): Promise<void> {
  const sessionEnd = new Date();
  const durationSeconds = Math.floor((sessionEnd.getTime() - sessionStartTime.getTime()) / 1000);

  const { error } = await supabase
    .from('chat_activity')
    .update({
      session_end: sessionEnd.toISOString(),
      session_duration_seconds: durationSeconds
    })
    .eq('id', sessionId);

  if (error) throw error;
}

/**
 * Get user's chat activity stats (for their own view)
 */
export async function getUserChatStats(userId: string): Promise<unknown> {
  const { data, error } = await supabase
    .from('chat_activity')
    .select('*')
    .eq('user_id', userId)
    .order('session_start', { ascending: false });

  if (error) throw error;

  // Calculate summary stats
  const totalSessions = data?.length ?? 0;
  const totalMessages = data?.reduce((sum, s) => sum + (s.message_count ?? 0), 0) ?? 0;
  const avgMessagesPerSession = totalSessions > 0 ? totalMessages / totalSessions : 0;
  const lastSession = data?.[0]?.session_start;

  return {
    totalSessions,
    totalMessages,
    avgMessagesPerSession,
    lastSession,
    sessions: data
  };
}


